/**
 * One design, one file per badge.
 *
 * The thing a group owner actually wants: eight ranks, eight PNGs, one shirt.
 * Doing it by hand means hiding seven layers, exporting, hiding a different
 * seven, exporting again — eight times, with no way to tell afterwards which
 * file was which except by opening them.
 *
 * ## Why this stores nothing
 *
 * `Variant` is a format concept and the packer already round-trips it, so the
 * obvious move is to save a rank set with the project. It is not done here, and
 * the reason is specific rather than lazy: `hydrate` deliberately does not
 * preserve layer ids — `addArtwork`/`addPlacement` mint their own — and a
 * variant's overrides are keyed by layer id. Saved and reloaded, every override
 * would point at a layer that no longer exists, and the failure is silent: the
 * rank set still opens, still exports, and every file comes out identical.
 *
 * Making that safe means remapping ids through `hydrate`, which is worth doing
 * and is not this. So a rank set is built and exported in one go: nothing is
 * stored, so nothing can go stale. The variant machinery is still what drives
 * it, which is what makes stored rank sets a later addition rather than a
 * rewrite.
 */
import type { Variant } from "../../shared/format";
import { soloOverrides, uniqueVariantName, variantFilename } from "./variants";

/** A layer offered as a badge. Just enough to name a file and solo a layer. */
export interface BadgeLayer {
  id: string;
  name: string;
}

export interface RankSetStep {
  /** The badge shown in this file. */
  layerId: string;
  /** What the badge is called, after clashes are resolved. */
  name: string;
  filename: string;
  /**
   * What to apply before composing: this badge visible, every other hidden.
   * Shaped as a `Variant`'s overrides so `applyVariant` drives it unchanged.
   */
  overrides: Variant["overrides"];
}

/**
 * The files a rank set will produce, worked out before any of them is made.
 *
 * Pure, and computed up front rather than as it goes, for two reasons. Names
 * have to be de-duplicated against each *other*, which cannot be done one at a
 * time. And an export that has already written three files before discovering
 * the fourth is impossible is worse than one that never started.
 */
export function planRankSet(badges: readonly BadgeLayer[], baseFilename: string): RankSetStep[] {
  const ids = badges.map((b) => b.id);
  const used: string[] = [];
  return badges.map((badge) => {
    /*
      Two layers called "Badge" are ordinary — duplicating one is how the
      second gets made. Left alone they produce one filename twice, and the
      second download silently replaces the first.
    */
    const name = uniqueVariantName(used, badge.name);
    used.push(name);
    return {
      layerId: badge.id,
      name,
      filename: variantFilename(baseFilename, name),
      // Every OTHER badge explicitly hidden — see `soloOverrides`. An override
      // that only turned this one on would leave the rest at their base
      // visibility, and every file would carry every badge.
      overrides: soloOverrides(ids, badge.id),
    };
  });
}

/**
 * The single file a rank set arrives as.
 *
 * A zip rather than eight downloads, and not for tidiness: browsers block
 * repeated automatic downloads from one gesture — Chrome prompts after the
 * second — so eight files from one click is a permission dialog followed by
 * seven files, or one file and seven silent failures. One download is also the
 * only way the eight arrive named and together.
 */
export function rankSetZipName(baseFilename: string): string {
  const stem = baseFilename.replace(/(-\d+x\d+)?\.png$/i, "") || "rank-set";
  return `${stem}-rank-set.zip`;
}

/**
 * Whether a rank set is worth offering at all.
 *
 * One badge is not a set — it is the export button. Below two, the whole flow
 * is a longer way to do something that already exists.
 */
export function canMakeRankSet(badges: readonly BadgeLayer[]): boolean {
  return badges.length >= 2;
}
